import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	DeliveryRatePayload,
	DeliveryRates,
	DeliveryRateState,
} from '../../pages/rates-management/types';
import { RootState } from '../store';
import deliveryService from '../../API/deliveryRatesApi';
import { toast } from 'react-toastify';

const initialState: DeliveryRateState = {
	data: [],
	isLoading: false,
};

export const asyncRetrieveDeliveryRates = createAsyncThunk<
	DeliveryRates[],
	void,
	{ state: RootState; rejectValue: string }
>('deliveryRate/retrieve', async (_, thunkApi) => {
	try {
		const res = await deliveryService.retrieveDeliveryRates();
		return res.data.data;
	} catch (error: any) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message & error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncCreateDeliveryRate = createAsyncThunk<
	DeliveryRates,
	DeliveryRatePayload,
	{ state: RootState; rejectedValue: string }
>('deliveryRate/create', async (payload, thunkApi) => {
	try {
		const res = await deliveryService.createDeliveryRate(payload);
		toast.success(res.data.message);
		return res.data.data;
	} catch (error: any) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncUpdateDeliveryRate = createAsyncThunk<
	{ id: string; updateRatePayload: Partial<DeliveryRatePayload> },
	{ id: string; updateRatePayload: Partial<DeliveryRatePayload> },
	{ state: RootState; rejectValue: string }
>('deliveryRate/update', async ({ id, updateRatePayload }, thunkApi) => {
	try {
		const res = await deliveryService.updateDeliveryRate(id, updateRatePayload);

		thunkApi.dispatch(asyncRetrieveDeliveryRates()); //**** just incase the state failed  refetch all delivery rates
		toast.success(res.data.message);
		return res.data.data;
	} catch (error: any) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncDeleteDeliveryRate = createAsyncThunk<
	string,
	string,
	{ state: RootState; rejectValue: string }
>('deliveryRate/delete', async (id, thunkApi) => {
	try {
		const res = await deliveryService.deleteDeliveryRate(id);
		thunkApi.dispatch(asyncRetrieveDeliveryRates());
		toast.success(res.data.message);
	} catch (error: any) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

const DeliveryRatesSlice = createSlice({
	name: 'DeliveryRates',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(asyncRetrieveDeliveryRates.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncRetrieveDeliveryRates.fulfilled,
				(state, action: PayloadAction<DeliveryRates[]>) => {
					state.isLoading = false;
					state.data = action.payload;
				},
			)
			.addCase(asyncRetrieveDeliveryRates.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncCreateDeliveryRate.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncCreateDeliveryRate.fulfilled,
				(state, action: PayloadAction<DeliveryRates>) => {
					state.isLoading = false;
					state.data = [action.payload, ...state.data];
				},
			)
			.addCase(asyncCreateDeliveryRate.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncUpdateDeliveryRate.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncUpdateDeliveryRate.fulfilled,
				(
					state,
					action: PayloadAction<{
						id: string;
						updateRatePayload: Partial<DeliveryRatePayload>;
					}>,
				) => {
					const { id, updateRatePayload } = action.payload;
					state.data = state.data.map((rate) =>
						rate.id === id ? { ...rate, ...updateRatePayload } : rate,
					);
					state.isLoading = false;
				},
			)
			.addCase(asyncUpdateDeliveryRate.rejected, (state, action) => {
				state.isLoading = false;
			})

			.addCase(asyncDeleteDeliveryRate.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncDeleteDeliveryRate.fulfilled, (state, action: PayloadAction<string>) => {
				state.data = state.data.filter((rate) => rate.id !== action.payload);
				state.isLoading = false;
			})
			.addCase(asyncDeleteDeliveryRate.rejected, (state) => {
				state.isLoading = false;
			});
	},
});

export const selectDeliveryRates = (state: RootState) => state.deliveryRates;

export default DeliveryRatesSlice.reducer;
